import {
    Badge,
    Box,
    Button,
    FileInput,
    Group,
    Modal,
    Paper,
    Select,
    SimpleGrid,
    Stack,
    Tabs,
    TagsInput,
    Text,
    TextInput,
    Textarea,
} from '@mantine/core';
import {
    getMediaGroups,
    getUserMediaGroups,
    getUsers,
} from '../../../../http/apiClient';
import { useEffect, useMemo } from 'react';

import { DATA_FILE_TYPES } from '../../../common/enums/enums';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { SearchableSelect } from '../../../common';
import { faSpinnerThird } from '@fortawesome/pro-solid-svg-icons';
import { genericValidator } from '../../../../utils/validation';
import { hasRole } from '../../../../utils/roles';
import useAddDataset from '../../../../http/mutations/data/useAddDataset';
import { useAuthStore } from '../../../../store';
import { useForm } from '@mantine/form';

const CreateDatasetModal = ({ existing, opened, close, onSuccess }) => {
    const userProfile = useAuthStore((state) => state.userProfile);
    const form = useForm({
        validateInputOnBlur: true,
        initialValues: {
            name: '',
            userId: hasRole(userProfile, 'SUPER_ADMIN')
                ? userProfile?.id.toString()
                : null,
            type: 'Normal',
            vertical: '',
        },

        validate: {
            name: (value) => {
                const { type, message } = genericValidator(
                    'Data set name',
                    value,
                    {
                        required: true,
                        minLength: 6,
                        maxLength: 60,
                        pattern: /^[a-zA-Z0-9_-]+$/,
                    }
                );

                return type === 'pattern'
                    ? 'Tags must only contain letters, numbers, hyphens and underscores'
                    : message;
            },
            trackingId: (value) => {
                const { type, message } = genericValidator(
                    'Tracking ID',
                    value,
                    {
                        required: false,
                        minLength: 4,
                        maxLength: 60,
                        pattern: /^[a-zA-Z0-9_-]+$/,
                    }
                );
                if (type) {
                    return type === 'pattern'
                        ? 'Tracking ID must only contain letters, numbers, hyphens and underscores'
                        : message;
                }
            },
            tags: (values) => {
                if (!values) {
                    return;
                }

                for (const value of values) {
                    const { type, message } = genericValidator('Tags', value, {
                        required: false,
                        minLength: 2,
                        maxLength: 60,
                        pattern: /^[a-zA-Z0-9_-]+$/,
                    });
                    if (type) {
                        return type === 'pattern'
                            ? 'Tags must only contain letters, numbers, hyphens and underscores'
                            : message;
                    }
                }
            },
        },
    });

    useEffect(() => {
        form.reset();
    }, [opened]);

    const { isPending, mutate: addOrUpdate } = useAddDataset();
    const onSubmit = (model) => {
        addOrUpdate(model, {
            onSuccess(data, variables, context) {
                if (onSuccess) {
                    onSuccess(data);
                }
                close();
            },
            onError(error, variables, context) {
                // setError(error);
            },
        });
    };

    return (
        <Modal.Root
            opened={opened}
            onClose={close}
            size={'md'}>
            <Modal.Overlay />
            <Modal.Content>
                <Modal.Header>
                    <Modal.Title>
                        {existing ? 'Update' : 'Add'} Data Set
                    </Modal.Title>
                    <Modal.CloseButton />
                </Modal.Header>
                <Modal.Body>
                    <form onSubmit={form.onSubmit(onSubmit)}>
                        <Stack gap={'xs'}>
                            <TextInput
                                label={'Data Set Name'}
                                placeholder='Enter a name'
                                withAsterisk
                                key={form.key('name')}
                                {...form.getInputProps('name')}
                            />

                            {hasRole(userProfile, 'SUPER_ADMIN') ? (
                                <>
                                    <SearchableSelect
                                        label={'User'}
                                        placeholder='User'
                                        queryHttpFn={getUsers}
                                        queryKey={'users'}
                                        optionsMapFn={(user) => ({
                                            label: user.username,
                                            value: user.id.toString(),
                                        })}
                                        withAsterisk
                                        {...form.getInputProps('userId')}
                                    />
                                </>
                            ) : null}

                            <Select
                                label={'Type'}
                                placeholder='Select file type'
                                data={DATA_FILE_TYPES.filter((x) =>
                                    hasRole(userProfile, 'SUPER_ADMIN')
                                        ? true
                                        : x.value === 'Normal' ||
                                          x.value === 'Clickers'
                                )}
                                withAsterisk
                                key={form.key('type')}
                                {...form.getInputProps('type')}
                            />

                            <TextInput
                                label={'Tracking ID'}
                                placeholder='Enter a tracking id'
                                key={form.key('trackingId')}
                                {...form.getInputProps('trackingId')}
                            />

                            <TagsInput
                                label={'Tags'}
                                placeholder={'Tags'}
                                key={form.key('tags')}
                                {...form.getInputProps('tags')}
                            />

                            <Group justify='flex-end'>
                                <Button
                                    type={'submit'}
                                    disabled={isPending || !form.isValid()}
                                    rightSection={
                                        isPending ? (
                                            <FontAwesomeIcon
                                                icon={faSpinnerThird}
                                                spin
                                            />
                                        ) : null
                                    }>
                                    {existing ? 'Update' : 'Create'}
                                </Button>
                            </Group>
                        </Stack>
                    </form>
                </Modal.Body>
            </Modal.Content>
        </Modal.Root>
    );
};
export default CreateDatasetModal;
